import React, {useCallback, useEffect, useRef, useState} from 'react';
import i18n from 'i18n';

interface Props {
    description: string;
}

const ChartCardDescription: React.FC<Props> = ({description}) => {
    const textRef = useRef<HTMLDivElement>(null);
    const [isExpanded, setIsExpanded] = useState(false);
    const [isTruncated, setIsTruncated] = useState(false);
    const normalizedDescription = description.replace(/\r\n/g, '\n');

    const updateTruncation = useCallback(() => {
        const element = textRef.current;
        if (!element || isExpanded) {
            return;
        }

        const explicitLineCount = normalizedDescription.split('\n').length;
        const overflowsClamp = element.scrollHeight > element.clientHeight + 1;
        setIsTruncated(explicitLineCount > 3 || overflowsClamp);
    }, [isExpanded, normalizedDescription]);

    useEffect(() => {
        setIsExpanded(false);
    }, [normalizedDescription]);

    useEffect(() => {
        updateTruncation();
    }, [normalizedDescription, updateTruncation]);

    useEffect(() => {
        const element = textRef.current;
        if (!element) {
            return;
        }

        const resizeObserver = new ResizeObserver(() => {
            updateTruncation();
        });
        resizeObserver.observe(element);

        return () => {
            resizeObserver.disconnect();
        };
    }, [normalizedDescription, updateTruncation]);

    const toggleExpanded = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setIsExpanded((expanded) => !expanded);
    };

    const showToggle = isTruncated || isExpanded;
    const showInlineToggle = showToggle && !isExpanded && isTruncated;

    return (
        <div className={[
            'chart-card-description-wrapper',
            showInlineToggle ? 'chart-card-description-wrapper--truncated' : '',
        ].filter(Boolean).join(' ')}>
            <div
                ref={textRef}
                className={`chart-card-description${isExpanded ? ' chart-card-description--expanded' : ''}`}
            >
                {normalizedDescription}
            </div>
            {showToggle ? (
                <button
                    type="button"
                    className={[
                        'chart-card-description-toggle',
                        showInlineToggle ? 'chart-card-description-toggle--inline' : '',
                    ].filter(Boolean).join(' ')}
                    onClick={toggleExpanded}
                >
                    {isExpanded ? i18n.t('chart_description_show_less.text') : i18n.t('jt_show_more.text')}
                </button>
            ) : null}
        </div>
    );
};

export default ChartCardDescription;
